// 根据环境自动选择后端地址
// 开发环境: npm run serve 时使用本地后端（支持局域网访问）
// 生产环境: npm run build 后使用线上后端
const isDev = process.env.NODE_ENV === 'development'
const DEFAULT_API_BASE_URL = 'https://hd.381611.xyz'

// 开发环境后端地址
// 优先使用环境变量，否则使用当前访问的主机名（支持局域网访问）
const DEV_API_URL = process.env.VUE_APP_API_BASE_URL ||
                    (isDev ? `http://${window.location.hostname}:8081` : '')

// 生产环境后端地址 - 优先使用环境变量，否则使用相对路径（同域部署）
// 可通过 VUE_APP_API_BASE_URL 环境变量配置
const PROD_API_URL = process.env.VUE_APP_API_BASE_URL || DEFAULT_API_BASE_URL

export const API_BASE_URL = isDev ? DEV_API_URL : PROD_API_URL

/**
 * 跳转到登录页面（同时关闭 WebSocket）
 */
function redirectToLogin() {
  // 关闭 WebSocket 连接
  try {
    const { chatWebSocket } = require('@/utils/websocket.js')
    chatWebSocket.close()
  } catch (e) {
    // ignore
  }

  // 清除本地存储的所有用户信息
  localStorage.removeItem('token')
  localStorage.removeItem('username')
  localStorage.removeItem('user')

  // 获取当前路径，避免在登录页重复跳转
  const currentPath = window.location.hash || window.location.pathname
  if (!currentPath.includes('/login')) {
    // 强制刷新页面并跳转到登录页
    window.location.href = '/#/login'
    window.location.reload()
  }
}

/**
 * 检查是否已登录
 */
export function isLoggedIn() {
  const token = localStorage.getItem('token')
  return !!token
}

/**
 * 判断是否是客户端公开聊天接口（不需要登录）
 * 注意：sessions、session/**、phrases/**、pinned/** 等是客服管理接口，需要登录
 */
function isPublicChatApi(path) {
  const publicChatPaths = [
    '/api/chat/verify-agent', '/api/chat/messages', '/api/chat/poll', '/api/chat/send',
    '/api/chat/online-status', '/api/chat/heartbeat', '/api/chat/agent-status',
    '/api/chat/upload-image', '/api/chat/image/', '/api/chat/read-position', '/api/chat/mark-read',
    '/api/chat/track-visit', '/api/chat/register-visit',
    '/api/chat/session/note', '/api/chat/session/'
  ]
  return publicChatPaths.some(p => path.includes(p))
}

/**
 * 统一封装请求方法
 * @param {string} path - 以 / 开头的路径，例如 '/api/login'
 * @param {RequestInit} options - fetch 的配置
 * @param {boolean} skipAuthCheck - 是否跳过认证检查（用于登录接口）
 */
export async function apiFetch(path, options = {}, skipAuthCheck = false) {
  // 始终直接请求后端：API_BASE_URL + path
  const base = API_BASE_URL.replace(/\/$/, '')
  const url = base + path

  const defaultHeaders = {
    'Content-Type': 'application/json'
  }

  // 添加 JWT token 到请求头
  const token = localStorage.getItem('token')
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`
  }

  // 非登录接口且没有 token，直接跳转登录页
  // 只有客户端公开聊天接口、订单接口、话术接口不需要登录
  if (!skipAuthCheck && !token && !path.includes('/auth/') && !isPublicChatApi(path) && !path.includes('/api/order/') && !path.startsWith('/api/phrases')) {
    redirectToLogin()
    throw new Error('未登录，请先登录')
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {})
    }
  })

  // 401 未授权，说明 token 无效或过期
  if (res.status === 401) {
    // 只有登录接口、聊天接口、订单接口、话术接口、防红接口、用户信息接口不需要跳转
    // 注意：只排除登录接口 /api/auth/login，其他 /api/auth/** 接口需要处理401
    const isLoginApi = path.includes('/api/auth/login')
    const isUserInfoApi = path.includes('/api/auth/me')
    const isPublicApi = isPublicChatApi(path) || path.includes('/api/order/') || path.startsWith('/api/phrases')
    const isAntiRedApi = path.includes('/api/user/api-links/') || path.includes('/api/custom-anti-red/')

    if (!skipAuthCheck && !isLoginApi && !isUserInfoApi && !isPublicApi && !isAntiRedApi) {
      // 尝试解析错误信息
      try {
        const errorData = await res.json()
        if (errorData.msg) {
          alert(errorData.msg)
        } else {
          alert('您的账号已在其他设备登录，请重新登录')
        }
      } catch (e) {
        alert('登录已过期，请重新登录')
      }
      redirectToLogin()
      throw new Error('登录已过期，请重新登录')
    }

    // 对于公开接口（聊天、订单等），无效token导致的401：清除token后自动重试一次（不带token）
    if ((isPublicApi || isAntiRedApi || isUserInfoApi) && token) {
      console.log('公开接口检测到无效token，清除后重试')
      localStorage.removeItem('token')

      // 不带 token 重新请求
      const retryHeaders = { ...defaultHeaders, ...(options.headers || {}) }
      delete retryHeaders['Authorization']
      const retryRes = await fetch(url, {
        ...options,
        headers: retryHeaders
      })
      if (!retryRes.ok) {
        throw new Error('网络错误: ' + retryRes.status)
      }
      return retryRes.json()
    }
  }

  // 403 权限不足
  if (res.status === 403) {
    try {
      const errorData = await res.json()
      // 40301: 用户已到期，自动退出登录
      if (errorData.code === 40301) {
        alert(errorData.msg || '您的账户已到期，请先充值续费后再使用')
        redirectToLogin()
        throw new Error('账户已到期')
      }
      return errorData // 返回错误信息让业务层处理
    } catch (e) {
      if (e.message === '账户已到期') throw e
      throw new Error('权限不足')
    }
  }

  if (!res.ok) {
    throw new Error('网络错误: ' + res.status)
  }

  return res.json()
}
